import { defineStore } from "pinia";
import socket from "@/services/socket";
import { useChatStore } from "./useChatStore";
import { useGrimoireStore } from "./useGrimoireStore";
import { useLocaleStore } from "./useLocaleStore";
import { usePlayersStore } from "./usePlayersStore";
import { useSessionStore } from "./useSessionStore";
import type {
  ChatMessage,
  SignalItem,
  SignalKind,
  SignalPayload,
  SignalTab,
} from "@/types";

interface SignalsState {
  targetPlayerId: string; // ST's currently targeted player
  draft: SignalItem[]; // composer model (ST compose or player response)
  incoming: SignalPayload | null; // a player's received signal awaiting an answer
}

function newId(): string {
  return Math.random().toString(36).slice(2);
}

export const useSignalsStore = defineStore("signals", {
  state: (): SignalsState => ({
    targetPlayerId: "",
    draft: [],
    incoming: null,
  }),

  actions: {
    openFor(playerId: string) {
      const grimoire = useGrimoireStore();
      this.targetPlayerId = playerId;
      this.clearDraft();
      if (grimoire.modal !== "signals") grimoire.toggleModal("signals");
    },

    addItem(item: SignalItem) {
      this.draft.push(item);
    },

    removeItem(index: number) {
      this.draft.splice(index, 1);
    },

    clearDraft() {
      this.draft = [];
    },

    send(kind: SignalKind, tabs?: SignalTab[]) {
      const session = useSessionStore();
      if (session.isPlayerOrSpectator || !this.targetPlayerId) return;
      const payload: SignalPayload = {
        id: newId(),
        kind,
        items: [...this.draft],
        ...(tabs ? { tabs: [...tabs] } : {}),
        fromId: "host",
        toId: this.targetPlayerId,
      };
      socket.sendDirect(this.targetPlayerId, "signal", payload);
      this.logToChat(payload);
      this.clearDraft();
      useGrimoireStore().toggleModal(null);
    },

    receiveSignal(payload: SignalPayload) {
      if (payload.kind === "ack") {
        useChatStore().markSignalAcknowledged(payload.id);
        return;
      }
      this.logToChat(payload);
      if (payload.kind === "response" && payload.replyTo) {
        useChatStore().markSignalAcknowledged(payload.replyTo);
      }
      const session = useSessionStore();
      if (
        session.isPlayerOrSpectator &&
        (payload.kind === "info" || payload.kind === "request")
      ) {
        this.clearDraft();
        this.incoming = payload;
      }
    },

    acknowledge() {
      if (!this.incoming) return;
      const session = useSessionStore();
      const originalId = this.incoming.id;
      const payload: SignalPayload = {
        id: originalId,
        kind: "ack",
        items: [],
        fromId: session.playerId,
        toId: "host",
      };
      socket.sendDirect("host", "signal", payload);
      this.clearDraft();
      this.incoming = null;
    },

    respond(items?: SignalItem[]) {
      if (!this.incoming) return;
      this.sendBack("response", items ?? [...this.draft]);
    },

    sendBack(kind: SignalKind, items: SignalItem[]) {
      const session = useSessionStore();
      const payload: SignalPayload = {
        id: newId(),
        kind,
        items,
        fromId: session.playerId,
        toId: "host",
        ...(this.incoming ? { replyTo: this.incoming.id } : {}),
      };
      socket.sendDirect("host", "signal", payload);
      this.logToChat(payload);
      this.clearDraft();
      this.incoming = null;
    },

    logToChat(payload: SignalPayload) {
      const chatStore = useChatStore();
      const msg: ChatMessage = {
        fromId: payload.fromId,
        fromName: "",
        toId: payload.toId,
        toName: "",
        text: this.signalToText(payload),
        isRead: false,
        signalId: payload.id,
        timestamp: Date.now(),
      };
      chatStore.receiveMessage(msg, 'logs');
    },

    signalToText(payload: SignalPayload): string {
      const t = useLocaleStore().t;
      if (payload.kind === "ack") return t("modal.signals.acknowledged");
      const text = payload.items.map((item) => this.itemToText(item)).join(", ");
      if (payload.kind === "request") {
        return `${text}`;
      }
      return text;
    },

    itemToText(item: SignalItem): string {
      const t = useLocaleStore().t;
      const value = item.value;
      switch (item.kind) {
        case "number":
        case "message":
          return String(value ?? "");
        case "player":
          {
            const playersStore = usePlayersStore();
            const session = useSessionStore();
            const player = playersStore.getById(String(value));
            const character = !session.isPlayerOrSpectator && player?.role ? `(${player!.realRole?.name || player!.role?.name})` : '';
            return `${player?.name || String(value ?? "")} ${character}`;
          }
        case "role":
          return useGrimoireStore().rolesJSONbyId.get(String(value))?.name || String(value ?? "");
        case "yes":
          return t("modal.signals.yes");
        case "no":
          return t("modal.signals.no");
        case "alignment":
          return t(`modal.signals.alignment.${value}`);
        case "type":
          return t(`modal.signals.type.${value}`);
        default:
          return "";
      }
    },
  },
});
